import type { FormGroup } from '@angular/forms';
import type { DropdownData, FilteredValue, Value } from '../../interfaces/input.interface';
import type {
    Form,
    ReactiveDropdownCatalogPhase,
    ReactiveFormSupportDataCacheStatus,
    ReactiveFormSupportDataContext,
    ReactiveFormSupportDataEndpoint,
    ReactiveFormSupportDataMetricsEvent,
    ReactiveFormSupportDataMetricsSink,
    ReactiveFormSupportDataParams,
    ReactiveFormSupportDataPhase,
    ReactiveFormSupportDataResult,
    ReactiveFormSupportDataSource,
    ReactiveFormSupportDataSourceKind,
    ReactiveFormSupportDataTarget,
} from '../../interfaces/form.interface';
import {
    baseRequest,
    baseSentence,
    type BuildTask,
    type DropdownLabelSelector,
} from '../reactive-request-builder.utils';
import type { ReactiveFormLoadPlan } from './reactive-form-load-orchestrator';

export type ReactiveFormSupportDataLoadPhase = ReactiveDropdownCatalogPhase | 'all';

export interface ReactiveFormSupportDataExecutorContext<TModel = unknown> {
    readonly source: ReactiveFormSupportDataSource<TModel>;
    readonly phase: ReactiveFormSupportDataLoadPhase;
    readonly formName: string | null;
    readonly onMetrics?: ReactiveFormSupportDataMetricsSink | undefined;
    readonly form?: Form<TModel> | undefined;
    readonly model?: FormGroup | null | undefined;
    readonly previousData?: TModel | null | undefined;
    readonly getValue: (fieldName: string) => unknown;
}

export type ReactiveFormSupportDataTaskFactory<TModel = unknown> = (
    context: ReactiveFormSupportDataExecutorContext<TModel>,
) => BuildTask | readonly BuildTask[] | null | undefined;

export interface ReactiveFormSupportDataExecutorOptions<TModel = unknown> {
    readonly form?: Form<TModel> | undefined;
    readonly sources?: readonly ReactiveFormSupportDataSource<TModel>[] | undefined;
    readonly loadPlan?: ReactiveFormLoadPlan | null | undefined;
    readonly phase?: ReactiveFormSupportDataLoadPhase | undefined;
    readonly defaultPhase?: ReactiveFormSupportDataPhase | undefined;
    readonly model?: FormGroup | null | undefined;
    readonly previousData?: TModel | null | undefined;
    readonly getValue?: ((fieldName: string) => unknown) | undefined;
    readonly createTasks?: ReactiveFormSupportDataTaskFactory<TModel> | undefined;
    readonly onMetrics?: ReactiveFormSupportDataMetricsSink | undefined;
    readonly setLoading?: ((loading: boolean) => void) | undefined;
    readonly toggleLoading?: boolean | undefined;
}

interface ReactiveFormSupportDataCacheEntry {
    readonly expiresAt: number;
    readonly staleExpiresAt: number;
    readonly value: unknown;
}

interface ReactiveFormSupportDataCacheLookup {
    readonly status: ReactiveFormSupportDataCacheStatus;
    readonly value?: unknown;
}

interface ReactiveFormSupportDataNetworkResult {
    readonly value: unknown;
    readonly networkMs: number;
}

interface ReactiveFormSupportDataFetchResult {
    readonly value: unknown;
    readonly cacheStatus: ReactiveFormSupportDataCacheStatus;
    readonly networkMs: number | null;
    readonly refresh?: Promise<ReactiveFormSupportDataNetworkResult>;
}

interface ReactiveFormSupportDataMetricsPayload {
    readonly sourceKind: ReactiveFormSupportDataSourceKind;
    readonly trigger: ReactiveFormSupportDataMetricsEvent['trigger'];
    readonly cacheStatus: ReactiveFormSupportDataCacheStatus;
    readonly startedAt: number;
    readonly networkMs: number | null;
    readonly ok: boolean;
    readonly error?: unknown;
}

interface ReactiveFormSupportDataStoredCacheEntry {
    readonly expiresAt?: unknown;
    readonly staleExpiresAt?: unknown;
    readonly value?: unknown;
}

const NO_CACHE_TTL_MS = 0;
const DEFAULT_STALE_TTL_MS = 86400000;
const EMPTY_PARAM_CACHE_KEY = '__empty__';
const UNDEFINED_PARAM_CACHE_KEY = '__undefined__';
const SUPPORT_DATA_STORAGE_PREFIX = 'fitogest.reactiveFormSupportData.';

const supportDataCache = new Map<string, ReactiveFormSupportDataCacheEntry>();

/** Executes support-data sources according to a ReactiveFormLoadPlan phase. */
export class ReactiveFormSupportDataExecutor {
    public static load<TModel>(options: ReactiveFormSupportDataExecutorOptions<TModel>): Promise<boolean> {
        const shouldToggleLoading = options.toggleLoading !== false;

        if (shouldToggleLoading) {
            options.setLoading?.(true);
        }

        return ReactiveFormSupportDataExecutor.runTasks(ReactiveFormSupportDataExecutor.createBuildTasks(options))
            .finally(() => {
                if (shouldToggleLoading) {
                    options.setLoading?.(false);
                }
            });
    }

    public static createBuildTasks<TModel>(options: ReactiveFormSupportDataExecutorOptions<TModel>): BuildTask[] {
        return ReactiveFormSupportDataExecutor.resolveSources(options)
            .filter((source) => ReactiveFormSupportDataExecutor.shouldLoadSource(source, options))
            .flatMap((source) => ReactiveFormSupportDataExecutor.createTasksForSource(source, options));
    }

    private static runTasks(tasks: readonly BuildTask[]): Promise<boolean> {
        if (tasks.length === 0) {
            return Promise.resolve(true);
        }

        return Promise.all(tasks.map((task) => ReactiveFormSupportDataExecutor.runTask(task)))
            .then((results) => results.every((result) => result));
    }

    private static runTask(task: BuildTask): Promise<boolean> {
        return new Promise<boolean>((resolve): void => {
            try {
                task((ok: boolean): void => {
                    resolve(ok === true);
                });
            } catch (error: unknown) {
                console.error('Error running support data task:', error);
                resolve(false);
            }
        }).catch((error: unknown): boolean => {
            console.error('Error processing support data task:', error);

            return false;
        });
    }

    private static resolveSources<TModel>(
        options: ReactiveFormSupportDataExecutorOptions<TModel>,
    ): readonly ReactiveFormSupportDataSource<TModel>[] {
        return options.sources ?? options.form?.supportDataSources ?? [];
    }

    private static shouldLoadSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        options: ReactiveFormSupportDataExecutorOptions<TModel>,
    ): boolean {
        const phase = options.phase ?? 'all';

        if (phase === 'all') {
            return true;
        }

        if (options.loadPlan) {
            return options.loadPlan.supportData[phase].some((item) => item.key === source.key);
        }

        return (source.phase ?? options.defaultPhase ?? 'deferred') === phase;
    }

    private static createTasksForSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        options: ReactiveFormSupportDataExecutorOptions<TModel>,
    ): BuildTask[] {
        const context = ReactiveFormSupportDataExecutor.createContext(source, options);
        const customTasks = options.createTasks?.(context);

        if (customTasks === null) {
            return [];
        }

        if (customTasks !== undefined) {
            return Array.isArray(customTasks) ? [...customTasks] : [customTasks];
        }

        return [ReactiveFormSupportDataExecutor.createSourceLoadTask(source, context)];
    }

    private static createContext<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        options: ReactiveFormSupportDataExecutorOptions<TModel>,
    ): ReactiveFormSupportDataExecutorContext<TModel> {
        return {
            source,
            phase: options.phase ?? 'all',
            formName: options.form?.name ?? null,
            onMetrics: options.onMetrics,
            form: options.form,
            model: options.model ?? null,
            previousData: options.previousData ?? null,
            getValue: options.getValue ?? ReactiveFormSupportDataExecutor.emptyValueGetter,
        };
    }

    private static createSourceLoadTask<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        context: ReactiveFormSupportDataExecutorContext<TModel>,
    ): BuildTask {
        return (resolve: (ok: boolean) => void): void => {
            ReactiveFormSupportDataExecutor.loadSource(source, context)
                .then(() => {
                    resolve(true);
                })
                .catch((error: unknown) => {
                    console.error(`Error loading support data source "${source.key}":`, error);
                    resolve(false);
                });
        };
    }

    private static async loadSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        context: ReactiveFormSupportDataExecutorContext<TModel>,
    ): Promise<unknown> {
        const supportContext = ReactiveFormSupportDataExecutor.toSupportDataContext(context);

        if (source.endpoint !== undefined) {
            return ReactiveFormSupportDataExecutor.loadDeclarativeSource(source, supportContext, context);
        }

        if (source.load !== undefined) {
            return ReactiveFormSupportDataExecutor.loadCustomSource(source, supportContext, context);
        }

        throw new Error(`Support data source "${source.key}" has no endpoint or load handler.`);
    }

    private static async loadCustomSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        supportContext: ReactiveFormSupportDataContext<TModel>,
        executorContext: ReactiveFormSupportDataExecutorContext<TModel>,
    ): Promise<unknown> {
        const startedAt = Date.now();

        try {
            const result = await source.load?.(supportContext);

            ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                sourceKind: 'custom',
                trigger: 'initial',
                cacheStatus: 'miss',
                startedAt,
                networkMs: null,
                ok: true,
            });

            return result;
        } catch (error: unknown) {
            ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                sourceKind: 'custom',
                trigger: 'initial',
                cacheStatus: 'miss',
                startedAt,
                networkMs: null,
                ok: false,
                error,
            });

            throw error;
        }
    }

    private static async loadDeclarativeSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        context: ReactiveFormSupportDataContext<TModel>,
        executorContext: ReactiveFormSupportDataExecutorContext<TModel>,
    ): Promise<ReactiveFormSupportDataResult> {
        const startedAt = Date.now();

        try {
            const params = ReactiveFormSupportDataExecutor.resolveParams(source.params, context);
            const result = await ReactiveFormSupportDataExecutor.fetchDeclarativeSource(source, params, context);
            const mappedResult = source.mapResponse?.(result.value, context) ?? result.value;

            ReactiveFormSupportDataExecutor.assignDeclarativeTarget(source, mappedResult, context);
            await source.onSuccess?.(mappedResult, context);
            ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                sourceKind: 'declarative',
                trigger: 'initial',
                cacheStatus: result.cacheStatus,
                startedAt,
                networkMs: result.networkMs,
                ok: true,
            });
            ReactiveFormSupportDataExecutor.scheduleDeclarativeRefresh(source, context, executorContext, result.cacheStatus, result.refresh);

            return mappedResult;
        } catch (error: unknown) {
            ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                sourceKind: 'declarative',
                trigger: 'initial',
                cacheStatus: 'miss',
                startedAt,
                networkMs: null,
                ok: false,
                error,
            });

            throw error;
        }
    }

    private static async fetchDeclarativeSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        params: Record<string, unknown> | undefined,
        context: ReactiveFormSupportDataContext<TModel>,
    ): Promise<ReactiveFormSupportDataFetchResult> {
        const cacheKey = ReactiveFormSupportDataExecutor.resolveCacheKey(source, params);
        const cached = ReactiveFormSupportDataExecutor.readCache(source, cacheKey);

        if (cached.status === 'fresh') {
            return {
                value: cached.value,
                cacheStatus: 'fresh',
                networkMs: null,
            };
        }

        if (cached.status === 'stale' && source.staleWhileRevalidate === true) {
            return {
                value: cached.value,
                cacheStatus: 'stale',
                networkMs: null,
                refresh: ReactiveFormSupportDataExecutor.fetchAndCacheDeclarativeSource(source, params, context, cacheKey),
            };
        }

        const networkResult = await ReactiveFormSupportDataExecutor.fetchAndCacheDeclarativeSource(source, params, context, cacheKey);

        return {
            value: networkResult.value,
            cacheStatus: 'miss',
            networkMs: networkResult.networkMs,
        };
    }

    private static async fetchAndCacheDeclarativeSource<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        params: Record<string, unknown> | undefined,
        context: ReactiveFormSupportDataContext<TModel>,
        cacheKey: string | null,
    ): Promise<ReactiveFormSupportDataNetworkResult> {
        const startedAt = Date.now();

        try {
            const response = await baseRequest(
                source.endpoint as ReactiveFormSupportDataEndpoint<unknown>,
                params,
                source.timeoutMs,
            );

            ReactiveFormSupportDataExecutor.writeCache(source, cacheKey, response);

            return {
                value: response,
                networkMs: ReactiveFormSupportDataExecutor.elapsedMs(startedAt),
            };
        } catch (error: unknown) {
            await source.onError?.(error, context);
            throw error;
        }
    }

    private static scheduleDeclarativeRefresh<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        context: ReactiveFormSupportDataContext<TModel>,
        executorContext: ReactiveFormSupportDataExecutorContext<TModel>,
        cacheStatus: ReactiveFormSupportDataCacheStatus,
        refresh: Promise<ReactiveFormSupportDataNetworkResult> | undefined,
    ): void {
        if (refresh === undefined) {
            return;
        }

        const startedAt = Date.now();

        void refresh
            .then(async (response) => {
                const mappedResult = source.mapResponse?.(response.value, context) ?? response.value;

                ReactiveFormSupportDataExecutor.assignDeclarativeTarget(source, mappedResult, context);
                await source.onSuccess?.(mappedResult, context);
                ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                    sourceKind: 'declarative',
                    trigger: 'background',
                    cacheStatus,
                    startedAt,
                    networkMs: response.networkMs,
                    ok: true,
                });
            })
            .catch((error: unknown) => {
                ReactiveFormSupportDataExecutor.emitMetrics(executorContext, {
                    sourceKind: 'declarative',
                    trigger: 'background',
                    cacheStatus,
                    startedAt,
                    networkMs: null,
                    ok: false,
                    error,
                });
                console.warn(`Background refresh failed for support data source "${source.key}":`, error);
            });
    }

    private static emitMetrics<TModel>(
        context: ReactiveFormSupportDataExecutorContext<TModel>,
        payload: ReactiveFormSupportDataMetricsPayload,
    ): void {
        const cacheHit = payload.cacheStatus !== 'miss';
        const metric: ReactiveFormSupportDataMetricsEvent = {
            formName: context.formName,
            sourceKey: context.source.key,
            sourceKind: payload.sourceKind,
            phase: context.phase,
            trigger: payload.trigger,
            cacheStatus: payload.cacheStatus,
            cacheHit,
            staleHit: payload.cacheStatus === 'stale',
            networkMs: payload.networkMs,
            phaseMs: ReactiveFormSupportDataExecutor.elapsedMs(payload.startedAt),
            ok: payload.ok,
            ...(context.source.diagnosticsThreshold === undefined ? {} : { diagnosticsThreshold: context.source.diagnosticsThreshold }),
            ...(payload.error === undefined ? {} : { error: payload.error }),
        };

        context.onMetrics?.(metric);
    }

    private static elapsedMs(startedAt: number): number {
        return Math.max(0, Date.now() - startedAt);
    }

    private static resolveParams<TModel>(
        params: ReactiveFormSupportDataParams<TModel> | undefined,
        context: ReactiveFormSupportDataContext<TModel>,
    ): Record<string, unknown> | undefined {
        if (params === undefined) {
            return undefined;
        }

        return typeof params === 'function' ? params(context) : params;
    }

    private static assignDeclarativeTarget<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        result: ReactiveFormSupportDataResult,
        context: ReactiveFormSupportDataContext<TModel>,
    ): void {
        const target = ReactiveFormSupportDataExecutor.resolveTarget(source.target, context);

        if (target === null) {
            return;
        }

        const data = ReactiveFormSupportDataExecutor.toDropdownData(source, result);
        const targets = Array.isArray(target) ? target : [target];

        targets.filter(Boolean).forEach((entry) => {
            ReactiveFormSupportDataExecutor.assignDropdownData(entry, data, source.preserveExistingValues !== false);
        });
    }

    private static resolveTarget<TModel>(
        target: ReactiveFormSupportDataTarget<TModel> | undefined,
        context: ReactiveFormSupportDataContext<TModel>,
    ): DropdownData<unknown> | readonly DropdownData<unknown>[] | null {
        if (target === undefined) {
            return null;
        }

        return typeof target === 'function' ? target(context) : target;
    }

    private static toDropdownData<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        result: ReactiveFormSupportDataResult,
    ): readonly FilteredValue<unknown>[] {
        if (ReactiveFormSupportDataExecutor.isDropdownData(result)) {
            return [...result.filtered];
        }

        const resultItems = ReactiveFormSupportDataExecutor.toItems(result);

        if (ReactiveFormSupportDataExecutor.isValueArray(resultItems)) {
            return resultItems.map((item) => ({
                ...item,
                raw: item.raw ?? item.value,
            }));
        }

        return baseSentence(
            resultItems,
            ReactiveFormSupportDataExecutor.resolveLabelSelector(source),
            source.noPlaceholder,
        ).map((item) => ({
            ...item,
            raw: item.raw ?? item.value,
        }));
    }

    private static resolveLabelSelector<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
    ): DropdownLabelSelector<unknown> {
        if (source.label !== undefined) {
            return source.label as DropdownLabelSelector<unknown>;
        }

        if (source.labelKeys !== undefined) {
            return source.labelKeys;
        }

        if (source.labelKey !== undefined) {
            return source.labelKey;
        }

        return ReactiveFormSupportDataExecutor.defaultLabelSelector;
    }

    private static defaultLabelSelector(item: unknown): unknown {
        if (item !== null && typeof item === 'object') {
            const record = item as Record<string, unknown>;

            return record['label'] ?? record['nombre'] ?? record['descripcion'] ?? record['id'] ?? '';
        }

        return item;
    }

    private static assignDropdownData(
        target: DropdownData<unknown>,
        data: readonly FilteredValue<unknown>[],
        preserveExistingValues: boolean,
    ): void {
        if (!preserveExistingValues) {
            target.filtered = [...data];
            target.values = [...data];

            return;
        }

        const existingValues = target.values ?? [];
        const newIds = new Set(data.map((item) => ReactiveFormSupportDataExecutor.getDropdownValueId(item)));
        const remainingItems = existingValues.filter((item) => !newIds.has(ReactiveFormSupportDataExecutor.getDropdownValueId(item)));
        const combinedValues = [...data, ...remainingItems];

        target.filtered = [...combinedValues];
        target.values = [...combinedValues];
    }

    private static getDropdownValueId(entry: Value<unknown> | FilteredValue<unknown>): unknown {
        const value = entry.value as Record<string, unknown> | null | undefined;

        return value?.['id'] ?? entry.label;
    }

    private static isDropdownData(value: unknown): value is DropdownData<unknown> {
        return value !== null && typeof value === 'object' && 'filtered' in value && 'values' in value;
    }

    private static isValueArray(value: readonly unknown[]): value is readonly Value<unknown>[] {
        return value.every((item) => item !== null && typeof item === 'object' && 'label' in item && 'value' in item);
    }

    private static toItems(value: ReactiveFormSupportDataResult): readonly unknown[] {
        if (Array.isArray(value)) {
            return value;
        }

        return [value];
    }

    private static resolveCacheKey<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        params: Record<string, unknown> | undefined,
    ): string | null {
        if (source.cacheKey === undefined || source.cacheKey.trim() === '') {
            return null;
        }

        return `${source.cacheKey}:${ReactiveFormSupportDataExecutor.getParamsCacheKey(params)}`;
    }

    private static readCache<TModel>(
        source: ReactiveFormSupportDataSource<TModel>,
        cacheKey: string | null,
    ): ReactiveFormSupportDataCacheLookup {
        if (cacheKey === null) {
            return { status: 'miss' };
        }

        const memoryLookup = ReactiveFormSupportDataExecutor.resolveCacheEntry(cacheKey, supportDataCache.get(cacheKey));

        if (memoryLookup.status !== 'miss') {
            return memoryLookup;
        }

        if (source.cacheStorage !== 'persistent') {
            return { status: 'miss' };
        }

        const persistentEntry = ReactiveFormSupportDataExecutor.readPersistentCache(cacheKey);
        const persistentLookup = ReactiveFormSupportDataExecutor.resolveCacheEntry(cacheKey, persistentEntry);

        if (persistentLookup.status !== 'miss' && persistentEntry !== null) {
            supportDataCache.set(cacheKey, persistentEntry);
        }

        return persistentLookup;
    }

    private static resolveCacheEntry(
        cacheKey: string,
        entry: ReactiveFormSupportDataCacheEntry | null | undefined,
    ): ReactiveFormSupportDataCacheLookup {
        if (entry === null || entry === undefined) {
            return { status: 'miss' };
        }

        const now = Date.now();

        if (entry.expiresAt > now) {
            return {
                status: 'fresh',
                value: entry.value,
            };
        }

        if (entry.staleExpiresAt > now) {
            return {
                status: 'stale',
                value: entry.value,
            };
        }

        supportDataCache.delete(cacheKey);
        ReactiveFormSupportDataExecutor.removePersistentCache(cacheKey);

        return { status: 'miss' };
    }

    private static writeCache<TModel>(source: ReactiveFormSupportDataSource<TModel>, cacheKey: string | null, value: unknown): void {
        const resolvedTtlMs = source.ttlMs ?? NO_CACHE_TTL_MS;

        if (cacheKey === null || resolvedTtlMs <= NO_CACHE_TTL_MS) {
            return;
        }

        const now = Date.now();
        const entry = {
            expiresAt: now + resolvedTtlMs,
            staleExpiresAt: now + Math.max(resolvedTtlMs, source.staleTtlMs ?? DEFAULT_STALE_TTL_MS),
            value,
        };

        supportDataCache.set(cacheKey, entry);

        if (source.cacheStorage === 'persistent') {
            ReactiveFormSupportDataExecutor.writePersistentCache(cacheKey, entry);
        }
    }

    private static readPersistentCache(cacheKey: string): ReactiveFormSupportDataCacheEntry | null {
        try {
            const storage = ReactiveFormSupportDataExecutor.getStorage();
            const rawEntry = storage?.getItem(ReactiveFormSupportDataExecutor.createPersistentCacheKey(cacheKey));

            if (rawEntry === null || rawEntry === undefined || rawEntry === '') {
                return null;
            }

            return ReactiveFormSupportDataExecutor.toCacheEntry(JSON.parse(rawEntry));
        } catch (error: unknown) {
            console.warn('Could not read persistent support data cache:', error);

            return null;
        }
    }

    private static writePersistentCache(cacheKey: string, entry: ReactiveFormSupportDataCacheEntry): void {
        try {
            ReactiveFormSupportDataExecutor.getStorage()?.setItem(
                ReactiveFormSupportDataExecutor.createPersistentCacheKey(cacheKey),
                JSON.stringify(entry),
            );
        } catch (error: unknown) {
            console.warn('Could not write persistent support data cache:', error);
        }
    }

    private static removePersistentCache(cacheKey: string): void {
        try {
            ReactiveFormSupportDataExecutor.getStorage()?.removeItem(ReactiveFormSupportDataExecutor.createPersistentCacheKey(cacheKey));
        } catch (error: unknown) {
            console.warn('Could not remove persistent support data cache:', error);
        }
    }

    private static createPersistentCacheKey(cacheKey: string): string {
        return `${SUPPORT_DATA_STORAGE_PREFIX}${cacheKey}`;
    }

    private static getStorage(): Storage | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        return localStorage;
    }

    private static toCacheEntry(value: unknown): ReactiveFormSupportDataCacheEntry | null {
        if (value === null || typeof value !== 'object') {
            return null;
        }

        const entry = value as ReactiveFormSupportDataStoredCacheEntry;

        if (typeof entry.expiresAt !== 'number' || typeof entry.staleExpiresAt !== 'number') {
            return null;
        }

        return {
            expiresAt: entry.expiresAt,
            staleExpiresAt: entry.staleExpiresAt,
            value: entry.value,
        };
    }

    private static getParamsCacheKey(params: Record<string, unknown> | undefined): string {
        if (params === undefined) {
            return UNDEFINED_PARAM_CACHE_KEY;
        }

        if (Object.keys(params).length === 0) {
            return EMPTY_PARAM_CACHE_KEY;
        }

        try {
            return JSON.stringify(params, Object.keys(params).sort());
        } catch {
            return String(params);
        }
    }

    private static toSupportDataContext<TModel>(
        context: ReactiveFormSupportDataExecutorContext<TModel>,
    ): ReactiveFormSupportDataContext<TModel> {
        return {
            model: context.model ?? null,
            previousData: context.previousData ?? null,
            getValue: context.getValue,
        };
    }

    private static emptyValueGetter(): unknown {
        return undefined;
    }
}
